const workspaceChangedStorageKey = "lenses.workspaceChanged";
const workspaceChangedChannelName = "lenses.workspaceChanged";
const workspaceSyncSourceId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export type WorkspaceChangedPayload = {
  enterpriseId: number;
  reason?: string;
  changedAt: string;
  sourceId?: string;
};

const getWorkspaceChangedChannel = () => {
  if (typeof window === "undefined" || typeof window.BroadcastChannel === "undefined") {
    return null;
  }
  return new window.BroadcastChannel(workspaceChangedChannelName);
};

export const notifyWorkspaceChanged = (enterpriseId: number, reason?: string) => {
  if (typeof window === "undefined") return;

  const payload: WorkspaceChangedPayload = {
    enterpriseId,
    reason,
    changedAt: new Date().toISOString(),
    sourceId: workspaceSyncSourceId,
  };

  window.localStorage.setItem(workspaceChangedStorageKey, JSON.stringify(payload));
  const channel = getWorkspaceChangedChannel();
  channel?.postMessage(payload);
  channel?.close();
  if (reason === "api-kpi-sync-progress") {
    window.dispatchEvent(new CustomEvent("lenses.kpiSyncProgress", { detail: payload }));
  }
};

export const subscribeToWorkspaceChanged = (
  enterpriseId: number | null,
  onChanged: (payload: WorkspaceChangedPayload) => void
) => {
  if (typeof window === "undefined" || enterpriseId === null) {
    return () => {};
  }

  const handlePayload = (payload?: Partial<WorkspaceChangedPayload> | null) => {
    if (!payload || payload.enterpriseId !== enterpriseId) return;
    if (payload.sourceId === workspaceSyncSourceId) return;
    onChanged(payload as WorkspaceChangedPayload);
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== workspaceChangedStorageKey || !event.newValue) return;
    try {
      handlePayload(JSON.parse(event.newValue) as WorkspaceChangedPayload);
    } catch {
      // Ignore malformed cross-tab messages.
    }
  };

  const channel = getWorkspaceChangedChannel();
  const handleChannelMessage = (event: MessageEvent<WorkspaceChangedPayload>) => {
    handlePayload(event.data);
  };

  window.addEventListener("storage", handleStorage);
  const handleSyncProgress = (event: Event) => {
    const payload = (event as CustomEvent<WorkspaceChangedPayload>).detail;
    if (payload?.enterpriseId === enterpriseId) onChanged(payload);
  };
  window.addEventListener("lenses.kpiSyncProgress", handleSyncProgress);
  channel?.addEventListener("message", handleChannelMessage);

  return () => {
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener("lenses.kpiSyncProgress", handleSyncProgress);
    channel?.removeEventListener("message", handleChannelMessage);
    channel?.close();
  };
};
